import React, { useState, useRef, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { Button, LoadPanel } from 'devextreme-react';
import Toolbar, { Item } from "devextreme-react/toolbar";
import { alert } from 'devextreme/ui/dialog';
import axios from "axios";
import {
    Column,
    DataGrid,
    ColumnChooser,
    ColumnChooserSearch,
    Selection,
    Export,
    Scrolling,
    StateStoring,
    FilterRow,
    Editing,
} from "devextreme-react/data-grid";
import { MESSAGES } from "../../../../../../common/Messages";
import {
    GRID_KEY,
    GRID_CONTAINER_ID,
    API_ENDPOINTS,
    EMPTY_GUID,
    COLUMN_CHOOSER,
    BUTTON_TEXTS,
    DEFAULT_VALUES,
    LOAD_PANEL,
    HTTP_HEADERS
} from "./constants";

function CreateTransactionCharges(props) {
    const { ClosePopup, TransactionHeaderRowId, Success } = props;
    var accessToken = props.oidc.user.access_token;
    const dataGridRef = useRef(null);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [chargesData, setChargesData] = useState([]);
    const position = { of: `#${GRID_CONTAINER_ID}` };

    // Load state from localStorage
    const loadState = useCallback(() => {
        try {
            const state = localStorage.getItem(GRID_KEY);
            return state ? JSON.parse(state) : null;
        } catch (e) {
            return null;
        }
    }, []);

    // Save state to localStorage
    const saveState = useCallback((state) => {
        try {
            localStorage.setItem(GRID_KEY, JSON.stringify(state));
        } catch (e) {
            console.error(MESSAGES.ERROR_SAVING_GRID_STATE, e);
        }
    }, []);

    // Fetch charges data
    const fetchCharges = useCallback(async () => {
        if (!TransactionHeaderRowId || TransactionHeaderRowId === EMPTY_GUID) {
            alert(MESSAGES.PLEASE_SELECT_TRANSACTION, MESSAGES.INFORMATION);
            return;
        }

        setLoadPanelVisible(true);
        try {
            const response = await axios.get(
                `${window._env_.REACT_APP_APIS}${API_ENDPOINTS.GET_AVAILABLE_CHARGES}`,
                {
                    params: {
                        transactionHeaderRowId: TransactionHeaderRowId
                    },
                    headers: {
                        'Content-Type': HTTP_HEADERS.CONTENT_TYPE,
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`
                    }
                }
            );

            if (response.data && Array.isArray(response.data)) {
                // Map API response to grid data format (API returns camelCase)
                const dataWithApply = response.data.map((item) => {
                    const applyValue = item.apply !== undefined ? item.apply : 0;
                    // Use rowId from API response (camelCase)
                    const rowId = item.rowId ? String(item.rowId) : null;
                    
                    if (!rowId) {
                        return null;
                    }
                    
                    return {
                        rowid: rowId,
                        chargeCode: item.chargeCode ? item.chargeCode.trim() : "",
                        description: item.description || "",
                        descReference: item.detailDesc || "",
                        customer: item.billToCode ? item.billToCode.trim() : "",
                        billAs: item.unitChargeFactor || DEFAULT_VALUES.BILL_AS,
                        rate: item.rate || DEFAULT_VALUES.RATE,
                        amount: (item.rate || DEFAULT_VALUES.RATE) * (item.unitChargeFactor || DEFAULT_VALUES.UNIT_CHARGE_FACTOR),
                        apply: applyValue === 1 || applyValue === true, // Convert int/boolean to boolean
                        // Store GUIDs for Apply button
                        rCharge: item.rCharge,
                        rRateschedulesource: rowId, // This is the rate schedule detail rowid (WMRateScheduleDetail.ROWID)
                        rBilltocustomer: item.rBillTo,
                        rGlaccount: item.rGlAccount,
                        rCaactivity: item.rCaActivity,
                        rGldepartment: item.rGlDepartment,
                        rCajob: item.rCaJob,
                        rGlfund: item.rGlFund
                    };
                }).filter(item => item !== null); // Remove any null entries
                setChargesData(dataWithApply);
            }
        } catch (error) {
            console.error(MESSAGES.ERROR_FETCHING_CHARGES, error);
            setChargesData([]);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [TransactionHeaderRowId, accessToken]);

    // Load charges when component mounts
    useEffect(() => {
        fetchCharges();
    }, [fetchCharges]);

    // Handle cell value change (for checkbox)
    const handleCellValueChanged = useCallback((e) => {
        if (e.dataField === 'apply') {
            const updatedData = chargesData.map(item => {
                if (item.rowid === e.data.rowid) {
                    return { ...item, apply: e.value };
                }
                return item;
            });
            setChargesData(updatedData);
        }
    }, [chargesData]);

    // Handle Apply button click
    const handleApply = useCallback(async () => {
        const selectedCharges = chargesData.filter(item => item.apply === true);
        
        if (selectedCharges.length === 0) {
            alert(MESSAGES.SELECT_AT_LEAST_ONE_CHARGE, MESSAGES.INFORMATION);
            return;
        }

        setLoadPanelVisible(true);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}${API_ENDPOINTS.APPLY_TRANSACTION_CHARGES}`,
                {
                    transactionHeaderRowId: TransactionHeaderRowId,
                    charges: selectedCharges.map(charge => ({
                        rCharge: charge.rCharge ? (typeof charge.rCharge === 'string' ? charge.rCharge : charge.rCharge.toString()) : null,
                        rRateschedulesource: charge.rRateschedulesource ? (typeof charge.rRateschedulesource === 'string' ? charge.rRateschedulesource : charge.rRateschedulesource.toString()) : null,
                        rBilltocustomer: charge.rBilltocustomer ? (typeof charge.rBilltocustomer === 'string' ? charge.rBilltocustomer : charge.rBilltocustomer.toString()) : null,
                        rGlaccount: charge.rGlaccount ? (typeof charge.rGlaccount === 'string' ? charge.rGlaccount : charge.rGlaccount.toString()) : null,
                        description: charge.description || "",
                        quantitybillas: charge.billAs || charge.quantitybillas || DEFAULT_VALUES.RATE,
                        rate: charge.rate || DEFAULT_VALUES.RATE,
                        amount: charge.amount || DEFAULT_VALUES.AMOUNT
                    }))
                },
                {
                    headers: {
                        'Content-Type': HTTP_HEADERS.CONTENT_TYPE,
                        access_token: accessToken,
                        Authorization: `Bearer ${accessToken}`
                    }
                }
            );

            if (response.data && response.data.success) {
                alert(MESSAGES.CHARGES_APPLIED_SUCCESS, MESSAGES.INFORMATION);
                if (Success) {
                    Success();
                }
                ClosePopup();
            } else {
                alert(response.data?.message || MESSAGES.ERROR_APPLYING_CHARGES, MESSAGES.ERROR);
            }
        } catch (error) {
            console.error(MESSAGES.ERROR_APPLYING_CHARGES, error);
            alert(error.response?.data?.message || MESSAGES.ERROR_APPLYING_CHARGES_DETAIL, MESSAGES.ERROR);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [chargesData, TransactionHeaderRowId, accessToken, Success, ClosePopup]);

    // Handle Cancel button click
    const handleCancel = useCallback(() => {
        ClosePopup();
    }, [ClosePopup]);

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    return (
        <div id={GRID_CONTAINER_ID} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <LoadPanel
                shadingColor={LOAD_PANEL.SHADING_COLOR}
                position={position}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={LOAD_PANEL.SHOW_INDICATOR}
                shading={LOAD_PANEL.SHADING}
                showPane={LOAD_PANEL.SHOW_PANE}
                hideOnOutsideClick={LOAD_PANEL.HIDE_ON_OUTSIDE_CLICK}
            />
            <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <DataGrid
                    ref={dataGridRef}
                    dataSource={chargesData}
                    showBorders={true}
                    rowAlternationEnabled={true}
                    allowColumnReordering={true}
                    allowColumnResizing={true}
                    columnAutoWidth={false}
                    onCellValueChanged={handleCellValueChanged}
                    onExporting={(e) => {
                        if (window.handleDevExtremeDataGridExport) {
                            window.handleDevExtremeDataGridExport(e);
                        }
                    }}
                    height="100%"
                    keyExpr="rowid"
                >
                    <StateStoring
                        enabled={true}
                        type="custom"
                        customLoad={loadState}
                        storageKey={GRID_KEY}
                        customSave={saveState}
                    />
                    <ColumnChooser
                        enabled={true}
                        title={COLUMN_CHOOSER.TITLE}
                        width={COLUMN_CHOOSER.WIDTH}
                        height={COLUMN_CHOOSER.HEIGHT}
                        sortOrder={COLUMN_CHOOSER.SORT_ORDER}
                    >
                        <ColumnChooserSearch
                            enabled={true}
                            editorOptions={{ placeholder: COLUMN_CHOOSER.SEARCH_PLACEHOLDER }} />
                    </ColumnChooser>
                    <Scrolling mode="standard" />
                    <Export enabled={true} allowExportSelectedData={true} />
                    <FilterRow visible={true} />
                    <Editing
                        mode="cell"
                        allowUpdating={true}
                        refreshMode="repaint"
                    />
                    
                    <Column 
                        dataField="apply" 
                        caption="Apply" 
                        dataType="boolean" 
                        width={60}
                        alignment="center"
                        allowEditing={true}
                    />
                    <Column 
                        dataField="chargeCode" 
                        caption="Charge Code" 
                        dataType="string"
                        width={100}
                    />
                    <Column 
                        dataField="description" 
                        caption="Description" 
                        dataType="string"
                        width={180}
                    />
                    <Column 
                        dataField="descReference" 
                        caption="Desc/Reference" 
                        dataType="string"
                        width={120}
                    />
                    <Column 
                        dataField="customer" 
                        caption="Customer" 
                        dataType="string"
                        width={100}
                    />
                    <Column 
                        dataField="billAs" 
                        caption="Bill As" 
                        dataType="number"
                        format="#,##0.00"
                        width={80}
                        alignment="right"
                    />
                    <Column 
                        dataField="rate" 
                        caption="Rate" 
                        dataType="number"
                        format="$#,##0.00"
                        width={100}
                        alignment="right"
                    />
                    <Column 
                        dataField="amount" 
                        caption="Amount" 
                        dataType="number"
                        format="$#,##0.00"
                        width={100}
                        alignment="right"
                    />
                </DataGrid>
            </div>
            <Toolbar style={{ marginTop: '10px', justifyContent: 'flex-start' }}>
                <Item location="before">
                    <Button
                        text={BUTTON_TEXTS.APPLY}
                        type="default"
                        className="accordianButton"
                        onClick={handleApply}
                    />
                </Item>
                <Item location="before">
                    <Button
                        text={BUTTON_TEXTS.CANCEL}
                        type="default"
                        className="accordianButton"
                        onClick={handleCancel}
                    />
                </Item>
            </Toolbar>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(CreateTransactionCharges);
